import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader, { DarkPage } from "@/components/app/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  Plus, Briefcase, Calendar, TrendingUp, Link2, Settings, GripVertical,
  Trophy, ArrowRight, Trash2, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";

const CURRENCIES = ["GBP", "USD", "EUR", "INR", "AUD", "CAD"];

const stageColour = (stage) => {
  if (stage.is_winning_stage) return "bg-[#00F5D4]/10 text-[#00F5D4] border-[#00F5D4]/50";
  if (stage.is_losing_stage) return "bg-[#FF007A]/10 text-[#FF007A] border-[#FF007A]/30";
  if (stage.probability_percentage >= 75) return "bg-[#00F5D4]/10 text-[#00F5D4] border-[#00F5D4]/30";
  if (stage.probability_percentage >= 50) return "bg-[#9CA3AF]/10 text-[#9CA3AF] border-[#9CA3AF]/30";
  if (stage.probability_percentage >= 25) return "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/30";
  return "bg-[#1F2937] text-[#6B7280] border-[#374151]";
};

const dialogCls = "rounded-none bg-[#111827] border-[#374151] text-white";
const inputCls = "rounded-none bg-[#030712] border-[#374151] text-white placeholder:text-[#4B5563] focus:ring-[#00F5D4]";
const labelCls = "text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]";
const btnPrimary = "rounded-none bg-[#00F5D4] hover:bg-[#00D4B8] text-[#030712] font-semibold";

const Deals = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [deals, setDeals] = useState([]);
  const [stages, setStages] = useState([]);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [view, setView] = useState(() => (typeof window !== "undefined" && window.innerWidth < 768 ? "list" : "kanban"));
  const [draggedDealId, setDraggedDealId] = useState(null);
  const [form, setForm] = useState({
    name: "", client: "", value: "", currency: "GBP", stage_id: "",
    target_margin_percentage: 35, scope_summary: "", expected_close: "", notes: "",
  });
  const launchParam = searchParams.get("launch");
  const handledLaunchRef = useRef(false);

  const isAdmin = user?.role === "owner" || user?.role === "admin";

  const load = useCallback(async () => {
    const [d, s, sum] = await Promise.all([
      api.get("/deals"),
      api.get("/pipeline-stages"),
      api.get("/deals/pipeline-summary").catch(() => ({ data: null })),
    ]);
    setDeals(d.data);
    setStages(s.data);
    setSummary(sum.data);
    setForm((f) => f.stage_id ? f : { ...f, stage_id: s.data[0]?.stage_id || "" });
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (launchParam && deals.length && !handledLaunchRef.current) {
      const target = deals.find((d) => d.deal_id === launchParam);
      if (target) {
        handledLaunchRef.current = true;
        if (target.linked_projects?.length || target.was_project_automated) {
          toast.success(`Project already provisioned for '${target.name}'`);
        } else {
          toast.info(`Open '${target.name}' to manage the auto-provisioned project.`);
        }
      }
    }
  }, [launchParam, deals]);

  const create = async () => {
    if (!form.name.trim()) return;
    if (!form.stage_id) return toast.error("Pick a stage");
    const payload = {
      name: form.name,
      client: form.client,
      value: form.value ? parseFloat(form.value) : 0,
      currency: form.currency,
      stage_id: form.stage_id,
      target_margin_percentage: parseFloat(form.target_margin_percentage) || 35,
      scope_summary: form.scope_summary,
      expected_close: form.expected_close || null,
      notes: form.notes,
    };
    const { data } = await api.post("/deals", payload);
    if (data.was_project_automated) {
      toast.success(`🎉 Project '${data.spawned_project_name}' auto-provisioned from this deal`);
    } else {
      toast.success("Deal added");
    }
    setOpen(false);
    setForm({ name: "", client: "", value: "", currency: "GBP", stage_id: stages[0]?.stage_id || "", target_margin_percentage: 35, scope_summary: "", expected_close: "", notes: "" });
    load();
  };

  const moveDealToStage = async (dealId, stageId) => {
    setDeals((p) => p.map((d) => (d.deal_id === dealId ? { ...d, stage_id: stageId } : d)));
    try {
      const { data } = await api.patch(`/deals/${dealId}`, { stage_id: stageId });
      if (data.was_project_automated && data.spawned_project_id) {
        toast.success(
          `🎉 Deal won → project '${data.spawned_project_name}' launched with 3 default phases.`,
          {
            action: {
              label: "Open project",
              onClick: () => window.location.assign(`/workspace/${data.spawned_project_id}`),
            },
            duration: 8000,
          }
        );
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Stage update failed");
      load();
    }
  };

  const deleteDeal = async (dealId) => {
    if (!window.confirm("Delete this deal? This cannot be undone.")) return;
    await api.delete(`/deals/${dealId}`);
    toast.success("Deal deleted");
    load();
  };

  const dealsByStage = useMemo(() => {
    const m = {};
    for (const s of stages) m[s.stage_id] = [];
    for (const d of deals) {
      const sid = d.stage_id;
      if (sid && m[sid]) m[sid].push(d);
    }
    return m;
  }, [deals, stages]);

  const onCardDragStart = (dealId) => () => setDraggedDealId(dealId);
  const onCardDragEnd = () => setDraggedDealId(null);
  const onColumnDragOver = (e) => e.preventDefault();
  const onColumnDrop = (stageId) => () => {
    if (draggedDealId) {
      const d = deals.find((x) => x.deal_id === draggedDealId);
      if (d && d.stage_id !== stageId) moveDealToStage(draggedDealId, stageId);
    }
    setDraggedDealId(null);
  };

  return (
    <DarkPage>
      <PageHeader
        eyebrow="Stakeholder & Project CRM"
        title="Deals Pipeline"
        description="Move a deal to a winning stage and CombinedWork auto-provisions a delivery project with three phases — instant handshake from sales to delivery."
        actions={
          <div className="flex items-center gap-2">
            <div className="flex border border-[#374151] rounded-none">
              <button
                onClick={() => setView("kanban")}
                className={`px-3 py-1.5 text-xs uppercase tracking-[0.2em] transition-colors ${view === "kanban" ? "bg-[#0D1117] text-[#030712]" : "bg-transparent text-[#6B7280] hover:text-white"}`}
                data-testid="view-kanban-btn"
              >Kanban</button>
              <button
                onClick={() => setView("list")}
                className={`px-3 py-1.5 text-xs uppercase tracking-[0.2em] transition-colors ${view === "list" ? "bg-[#0D1117] text-[#030712]" : "bg-transparent text-[#6B7280] hover:text-white"}`}
                data-testid="view-list-btn"
              >List</button>
            </div>
            {isAdmin && (
              <Button
                variant="outline"
                className="rounded-none border-[#374151] text-[#9CA3AF] hover:text-white bg-transparent"
                onClick={() => setSettingsOpen(true)}
                data-testid="pipeline-settings-btn"
              ><Settings className="w-4 h-4 mr-1" /> Stages</Button>
            )}
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className={btnPrimary} data-testid="new-deal-btn">
                  <Plus className="w-4 h-4 mr-1" /> New deal
                </Button>
              </DialogTrigger>
              <DialogContent className={`${dialogCls} sm:max-w-xl`}>
                <DialogHeader><DialogTitle className="font-display tracking-tight text-white">New deal</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className={labelCls}>Deal name</Label>
                      <Input className={`${inputCls} mt-1`} data-testid="deal-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                    </div>
                    <div>
                      <Label className={labelCls}>Client</Label>
                      <Input className={`${inputCls} mt-1`} data-testid="deal-client-input" value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className={labelCls}>Value</Label>
                      <Input type="number" className={`${inputCls} mt-1`} placeholder="e.g. 50000" data-testid="deal-value-input" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
                    </div>
                    <div>
                      <Label className={labelCls}>Currency</Label>
                      <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                        <SelectTrigger className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white" data-testid="deal-currency-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#111827] border-[#374151] text-white">{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className={labelCls}>Target margin %</Label>
                      <Input type="number" min="0" max="100" step="0.5" className={`${inputCls} mt-1`} data-testid="deal-margin-input" value={form.target_margin_percentage} onChange={(e) => setForm({ ...form, target_margin_percentage: e.target.value })} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className={labelCls}>Stage</Label>
                      <Select value={form.stage_id} onValueChange={(v) => setForm({ ...form, stage_id: v })}>
                        <SelectTrigger className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white" data-testid="deal-stage-select"><SelectValue placeholder="Pick stage" /></SelectTrigger>
                        <SelectContent className="bg-[#111827] border-[#374151] text-white">{stages.map((s) => <SelectItem key={s.stage_id} value={s.stage_id}>{s.stage_name} ({s.probability_percentage}%)</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className={labelCls}>Expected close</Label>
                      <Input type="date" className={`${inputCls} mt-1`} data-testid="deal-close-input" value={form.expected_close} onChange={(e) => setForm({ ...form, expected_close: e.target.value })} />
                    </div>
                  </div>
                  <div>
                    <Label className={labelCls}>Scope summary (used when deal converts to project)</Label>
                    <Textarea rows={3} className={`${inputCls} mt-1 font-mono text-xs`} placeholder="Optional. Pipeline notes, SOW bullets, client requirements." data-testid="deal-scope-input" value={form.scope_summary} onChange={(e) => setForm({ ...form, scope_summary: e.target.value })} />
                  </div>
                  <div>
                    <Label className={labelCls}>Notes</Label>
                    <Textarea className={`${inputCls} mt-1`} data-testid="deal-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={create} className={btnPrimary} data-testid="confirm-create-deal">Add deal</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      <div className="px-5 sm:px-8 lg:px-12 py-8 space-y-8">
        {/* KPI strip */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-[#1F2937] border border-[#1F2937]">
          <div className="bg-[#111827] p-6">
            <div className="flex items-center justify-between"><Briefcase className="w-4 h-4 text-[#4B5563]" /><div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">Open Pipeline</div></div>
            <div className="font-display font-black text-4xl tracking-tighter mt-2" data-testid="kpi-open-value">£{(summary?.open_value || 0).toLocaleString()}</div>
          </div>
          <div className="bg-[#111827] p-6">
            <div className="flex items-center justify-between"><Sparkles className="w-4 h-4 text-[#00F5D4]" /><div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">Weighted Forecast</div></div>
            <div className="font-display font-black text-4xl tracking-tighter mt-2 text-[#00F5D4]" data-testid="kpi-weighted-forecast">£{(summary?.grand_weighted_forecast || 0).toLocaleString()}</div>
          </div>
          <div className="bg-[#111827] p-6">
            <div className="flex items-center justify-between"><Trophy className="w-4 h-4 text-[#00F5D4]" /><div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">Won</div></div>
            <div className="font-display font-black text-4xl tracking-tighter mt-2 text-[#00F5D4]" data-testid="kpi-won-value">£{(summary?.won_value || 0).toLocaleString()}</div>
          </div>
          <div className="bg-[#111827] p-6">
            <div className="flex items-center justify-between"><TrendingUp className="w-4 h-4 text-[#4B5563]" /><div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">Total Raw Value</div></div>
            <div className="font-display font-black text-4xl tracking-tighter mt-2" data-testid="kpi-total-raw">£{(summary?.grand_total_value || 0).toLocaleString()}</div>
          </div>
        </div>

        {view === "kanban" ? (
          <div className="overflow-x-auto -mx-5 sm:mx-0 px-5 sm:px-0">
            <div className="grid gap-px bg-[#1F2937] border border-[#1F2937] min-h-[60vh]" style={{ gridTemplateColumns: `repeat(${Math.max(stages.length, 1)}, minmax(220px, 1fr))` }}>
            {stages.map((stage) => {
              const colDeals = dealsByStage[stage.stage_id] || [];
              return (
                <div
                  key={stage.stage_id}
                  className="bg-[#0D1117]"
                  data-testid={`stage-col-${stage.stage_id}`}
                  onDragOver={onColumnDragOver}
                  onDrop={onColumnDrop(stage.stage_id)}
                >
                  <div className={`px-4 py-3 border-b ${stageColour(stage)} font-display font-bold text-sm tracking-tight flex items-center justify-between`}>
                    <span>{stage.stage_name}</span>
                    <span className="text-xs font-semibold tabular-nums">{stage.probability_percentage}% · ({colDeals.length})</span>
                  </div>
                  <div className="p-3 space-y-2 min-h-[200px]">
                    {colDeals.map((d) => (
                      <div
                        key={d.deal_id}
                        draggable
                        onDragStart={onCardDragStart(d.deal_id)}
                        onDragEnd={onCardDragEnd}
                        className={`bg-[#111827] border border-[#1F2937] p-3 hover:border-[#374151] cursor-move transition-opacity ${
                          draggedDealId === d.deal_id ? "opacity-40" : ""
                        }`}
                        data-testid={`deal-card-${d.deal_id}`}
                      >
                        <div className="flex items-start gap-2">
                          <GripVertical className="w-3 h-3 text-[#374151] mt-1 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm truncate text-white">{d.name}</div>
                            {d.client && <div className="text-xs text-[#9CA3AF] mt-0.5">{d.client}</div>}
                            <div className="mt-2 text-[13px] font-display font-bold tracking-tight text-white" data-testid={`deal-value-${d.deal_id}`}>
                              {d.currency || "GBP"} {(d.value || 0).toLocaleString()}
                            </div>
                            <div className="mt-1 text-[10px] text-[#6B7280] font-mono">
                              {d.target_margin_percentage || 35}% margin target
                            </div>
                            {d.expected_close && (
                              <div className="text-[10px] text-[#6B7280] font-mono mt-1">▸ {new Date(d.expected_close).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</div>
                            )}
                            {stage.is_winning_stage && (
                              <div className="mt-2 text-[10px] uppercase tracking-[0.2em] text-[#00F5D4] flex items-center gap-1">
                                <Trophy className="w-3 h-3" /> Auto-provisioned
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    {colDeals.length === 0 && <div className="text-[11px] text-[#4B5563] text-center py-4">Drop deals here</div>}
                  </div>
                </div>
              );
            })}
            </div>
          </div>
        ) : (
          <div className="border border-[#1F2937] bg-[#111827] overflow-x-auto">
            <div className="min-w-[600px]">
            <div className="grid grid-cols-12 gap-3 px-5 py-2 bg-[#1F2937] border-b border-[#1F2937] text-[10px] uppercase tracking-[0.25em] text-[#6B7280]">
              <div className="col-span-3">Deal</div>
              <div className="col-span-2">Client</div>
              <div className="col-span-2">Stage</div>
              <div className="col-span-2 text-right">Value</div>
              <div className="col-span-1 text-right">Margin</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            {deals.map((d) => (
              <div key={d.deal_id} className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-[#1F2937] items-center text-sm" data-testid={`deal-row-${d.deal_id}`}>
                <div className="col-span-3 font-medium truncate">{d.name}</div>
                <div className="col-span-2 text-[#9CA3AF] truncate">{d.client || "—"}</div>
                <div className="col-span-2">
                  <Select value={d.stage_id || ""} onValueChange={(v) => moveDealToStage(d.deal_id, v)}>
                    <SelectTrigger className="rounded-none h-7 text-xs bg-[#030712] border-[#374151] text-white" data-testid={`deal-stage-${d.deal_id}`}><SelectValue placeholder="Pick" /></SelectTrigger>
                    <SelectContent className="bg-[#111827] border-[#374151] text-white">{stages.map((s) => <SelectItem key={s.stage_id} value={s.stage_id}>{s.stage_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 text-right font-mono">{d.currency || "GBP"} {(d.value || 0).toLocaleString()}</div>
                <div className="col-span-1 text-right text-xs text-[#9CA3AF]">{d.target_margin_percentage || 35}%</div>
                <div className="col-span-2 text-right">
                  <button onClick={() => deleteDeal(d.deal_id)} className="text-[#4B5563] hover:text-[#FF007A] p-2 -m-1 inline-flex items-center justify-center" data-testid={`delete-deal-${d.deal_id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
            {deals.length === 0 && <div className="px-5 py-12 text-center text-sm text-[#6B7280]">No deals yet. Click &quot;New deal&quot; to start.</div>}
            </div>
          </div>
        )}

        <div className="text-xs text-[#6B7280] flex items-center gap-2">
          <Link2 className="w-3 h-3" />
          Drag a deal into a winning stage and a delivery project is auto-provisioned with three default phases. Click <ArrowRight className="w-3 h-3 inline" /> &quot;Open project&quot; in the toast to jump in.
        </div>
      </div>

      {settingsOpen && (
        <PipelineSettings
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          stages={stages}
          onChange={load}
        />
      )}
    </DarkPage>
  );
};


// =========================================================================
// Pipeline Settings
// =========================================================================
const PipelineSettings = ({ open, onClose, stages, onChange }) => {
  const [adding, setAdding] = useState(false);
  const [newStage, setNewStage] = useState({ stage_name: "", probability_percentage: 50, is_winning_stage: false, is_losing_stage: false });
  const [editing, setEditing] = useState({});

  const inputCls = "rounded-none bg-[#030712] border-[#374151] text-white placeholder:text-[#4B5563] focus:ring-[#00F5D4]";

  const saveEdit = async (stage_id) => {
    const draft = editing[stage_id];
    if (!draft) return;
    try {
      await api.patch(`/pipeline-stages/${stage_id}`, draft);
      toast.success("Stage updated");
      setEditing((e) => { const n = { ...e }; delete n[stage_id]; return n; });
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const remove = async (stage_id) => {
    if (!window.confirm("Delete this stage?")) return;
    try {
      await api.delete(`/pipeline-stages/${stage_id}`);
      toast.success("Stage deleted");
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const addStage = async () => {
    if (!newStage.stage_name.trim()) return toast.error("Name required");
    try {
      await api.post("/pipeline-stages", newStage);
      toast.success("Stage added");
      setAdding(false);
      setNewStage({ stage_name: "", probability_percentage: 50, is_winning_stage: false, is_losing_stage: false });
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Add failed"); }
  };

  const move = async (idx, dir) => {
    const newOrder = [...stages];
    const swap = idx + dir;
    if (swap < 0 || swap >= newOrder.length) return;
    [newOrder[idx], newOrder[swap]] = [newOrder[swap], newOrder[idx]];
    try {
      await api.post("/pipeline-stages/reorder", newOrder.map((s) => s.stage_id));
      onChange();
    } catch (e) { toast.error("Reorder failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-none bg-[#111827] border-[#374151] text-white sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight text-white">Pipeline Stages</DialogTitle>
          <DialogDescription className="text-xs text-[#9CA3AF]">
            Reorder, edit, add or remove stages. Mark a stage as <strong className="text-white">Winning</strong> to trigger automatic project provisioning when a deal lands in it.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto" data-testid="pipeline-settings-list">
          {stages.map((s, idx) => {
            const draft = editing[s.stage_id];
            return (
              <div key={s.stage_id} className="border border-[#1F2937] p-3 bg-[#0D1117]" data-testid={`stage-row-${s.stage_id}`}>
                <div className="flex items-center gap-2">
                  <div className="flex flex-col">
                    <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-xs text-[#6B7280] disabled:opacity-30">▲</button>
                    <button onClick={() => move(idx, 1)} disabled={idx === stages.length - 1} className="text-xs text-[#6B7280] disabled:opacity-30">▼</button>
                  </div>
                  {draft ? (
                    <>
                      <Input className={`${inputCls} flex-1`} value={draft.stage_name ?? s.stage_name} onChange={(e) => setEditing((m) => ({ ...m, [s.stage_id]: { ...draft, stage_name: e.target.value } }))} />
                      <Input type="number" min="0" max="100" className={`${inputCls} w-20`} value={draft.probability_percentage ?? s.probability_percentage} onChange={(e) => setEditing((m) => ({ ...m, [s.stage_id]: { ...draft, probability_percentage: parseFloat(e.target.value) } }))} />
                      <Button size="sm" className="rounded-none bg-[#00F5D4] hover:bg-[#00D4B8] text-[#030712] font-semibold" onClick={() => saveEdit(s.stage_id)} data-testid={`save-stage-${s.stage_id}`}>Save</Button>
                      <Button size="sm" variant="outline" className="rounded-none border-[#374151] text-[#9CA3AF] bg-transparent" onClick={() => setEditing((m) => { const n = { ...m }; delete n[s.stage_id]; return n; })}>Cancel</Button>
                    </>
                  ) : (
                    <>
                      <div className="flex-1">
                        <div className="text-sm font-medium">{s.stage_name}</div>
                        <div className="text-xs text-[#6B7280] font-mono">
                          {s.probability_percentage}% ·
                          {s.is_winning_stage ? " WINNING" : s.is_losing_stage ? " LOSING" : " open"}
                        </div>
                      </div>
                      <Button size="sm" variant="outline" className="rounded-none border-[#374151] text-[#9CA3AF] bg-transparent hover:text-white" onClick={() => setEditing((m) => ({ ...m, [s.stage_id]: {} }))} data-testid={`edit-stage-${s.stage_id}`}>Edit</Button>
                      <button onClick={() => remove(s.stage_id)} className="text-[#4B5563] hover:text-[#FF007A] p-2 -m-1 inline-flex items-center justify-center" data-testid={`delete-stage-${s.stage_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {adding ? (
          <div className="border-t border-[#1F2937] pt-3 space-y-2" data-testid="add-stage-form">
            <Input className={inputCls} placeholder="Stage name (e.g. Discovery)" value={newStage.stage_name} onChange={(e) => setNewStage({ ...newStage, stage_name: e.target.value })} data-testid="new-stage-name-input" />
            <div className="flex items-center gap-2">
              <Input type="number" min="0" max="100" className={`${inputCls} w-28`} value={newStage.probability_percentage} onChange={(e) => setNewStage({ ...newStage, probability_percentage: parseFloat(e.target.value) })} data-testid="new-stage-prob-input" />
              <label className="text-xs text-[#9CA3AF] flex items-center gap-1"><input type="checkbox" checked={newStage.is_winning_stage} onChange={(e) => setNewStage({ ...newStage, is_winning_stage: e.target.checked, is_losing_stage: e.target.checked ? false : newStage.is_losing_stage })} data-testid="new-stage-winning-cb" className="accent-[#00F5D4]" /> Winning</label>
              <label className="text-xs text-[#9CA3AF] flex items-center gap-1"><input type="checkbox" checked={newStage.is_losing_stage} onChange={(e) => setNewStage({ ...newStage, is_losing_stage: e.target.checked, is_winning_stage: e.target.checked ? false : newStage.is_winning_stage })} className="accent-[#FF007A]" /> Losing</label>
              <div className="ml-auto flex gap-2">
                <Button size="sm" variant="outline" className="rounded-none border-[#374151] text-[#9CA3AF] bg-transparent" onClick={() => setAdding(false)}>Cancel</Button>
                <Button size="sm" className="rounded-none bg-[#00F5D4] hover:bg-[#00D4B8] text-[#030712] font-semibold" onClick={addStage} data-testid="confirm-add-stage">Add stage</Button>
              </div>
            </div>
          </div>
        ) : (
          <Button variant="outline" className="rounded-none border-[#374151] text-[#9CA3AF] bg-transparent hover:text-white" onClick={() => setAdding(true)} data-testid="add-stage-btn">
            <Plus className="w-4 h-4 mr-1" /> Add stage
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default Deals;
